import React from 'react';
import type { Notification } from '../../context/NotificationContext';

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
}

// Format relative time (e.g., "5 min ago", "2 hours ago", "Yesterday")
function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return date.toLocaleDateString();
}

// Truncate message with ellipsis
function truncateMessage(message: string, maxLength: number = 80): string {
  if (message.length <= maxLength) return message;
  return message.substring(0, maxLength).trim() + '...';
}

export const NotificationItem: React.FC<NotificationItemProps> = ({ notification, onClick }) => {
  const handleClick = () => {
    onClick(notification);
  };

  return (
    <div
      onClick={handleClick}
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e2e8f0',
        cursor: 'pointer',
        backgroundColor: notification.isRead ? '#fff' : '#f0f9ff',
        transition: 'background-color 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = notification.isRead ? '#f8fafc' : '#e0f2fe';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = notification.isRead ? '#fff' : '#f0f9ff';
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        gap: '8px',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: notification.isRead ? '400' : '600',
            fontSize: '14px',
            color: '#1e293b',
            marginBottom: '4px',
          }}>
            {notification.title}
          </div>
          <div style={{
            fontSize: '13px',
            color: '#64748b',
            lineHeight: '1.4',
          }}>
            {truncateMessage(notification.message)}
          </div>
        </div>
        <div style={{
          fontSize: '12px',
          color: '#94a3b8',
          whiteSpace: 'nowrap',
          flexShrink: 0,
        }}>
          {formatRelativeTime(notification.createdAt)}
        </div>
      </div>
    </div>
  );
};

export default NotificationItem;
